import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, MessageCircle, BadgeCheck } from 'lucide-react';

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0B2545] text-white relative overflow-hidden">
      {/* Soft accent glow */}
      <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-[#4DD4AC]/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 py-10 md:py-12 relative">
        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-6 md:gap-8">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-4">
            <img
              src="/img/c9mjttd5_logo.webp"
              alt="Patrick's Edu Care"
              className="h-10 w-auto mb-3 brightness-0 invert"
            />
            <p className="text-sm text-white/70 mb-4 max-w-xs leading-relaxed">
              Care for your global future. Trusted partner in turning study-abroad dreams into reality.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <BadgeCheck className="text-[#4DD4AC]" size={14} />
              <span className="text-[11px] font-semibold tracking-wide">ISO 9001:2015 Certified</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#4DD4AC] mb-3">Explore</h3>
            <ul className="space-y-2 text-sm">
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About' },
                { to: '/programs', label: 'Programs' },
                { to: '/services', label: 'Services' },
                { to: '/gallery', label: 'Gallery' },
                { to: '/contact', label: 'Contact' }
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/70 hover:text-[#4DD4AC] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs Quick Links */}
          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#4DD4AC] mb-3">Programs</h3>
            <ul className="space-y-2 text-sm">
              {[
                { slug: 'mbbs', label: 'MBBS' },
                { slug: 'bsc-nursing', label: 'BSc Nursing' },
                { slug: 'dentistry', label: 'Dentistry' },
                { slug: 'artificial-intelligence', label: 'AI' },
                { slug: 'mba', label: 'MBA' },
                { slug: 'bba', label: 'BBA' }
              ].map((p) => (
                <li key={p.slug}>
                  <Link to={`/programs/${p.slug}`} className="text-white/70 hover:text-[#4DD4AC] transition-colors">
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact compact */}
          <div className="col-span-2 md:col-span-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#4DD4AC] mb-3">Reach Us</h3>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-2.5 text-white/75 leading-snug">
                <MapPin size={15} className="text-[#4DD4AC] mt-0.5 flex-shrink-0" />
                <span>2nd Floor, Parur TK Building, M.G-Road, Ravipuram, Ernakulam, Kerala 682016</span>
              </li>
              <li>
                <a href="tel:+917594881898" className="flex items-center gap-2.5 text-white/85 hover:text-[#4DD4AC] transition-colors">
                  <Phone size={15} className="text-[#4DD4AC]" /> +91 75948 81898
                </a>
              </li>
              <li>
                <a href="mailto:info@patrickseducare.com" className="flex items-center gap-2.5 text-white/85 hover:text-[#4DD4AC] transition-colors">
                  <Mail size={15} className="text-[#4DD4AC]" /> info@patrickseducare.com
                </a>
              </li>
            </ul>

            {/* Social Icons */}
            <div className="flex items-center gap-2 mt-4">
              {[
                { href: 'https://www.facebook.com/share/1BMiCJZqdk/?mibextid=wwXIfr', icon: Facebook, label: 'Facebook' },
                { href: 'https://www.instagram.com/patricks_educare?igsh=MXU4Mmkxano2a25vdQ%3D%3D&utm_source=qr', icon: Instagram, label: 'Instagram' },
                { href: 'https://www.linkedin.com/company/patrick-s-edu-care-pvt-ltd/', icon: Linkedin, label: 'LinkedIn' },
                { href: 'https://wa.me/917558881898?text=Hi%20Patrick%27s%20Edu%20Care%2C%20I%27d%20like%20to%20know%20more%20about%20studying%20abroad.', icon: MessageCircle, label: 'WhatsApp', hover: '#25D366' }
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#4DD4AC] hover:border-[#4DD4AC] transition-colors"
                  style={s.hover ? { '--hover': s.hover } : undefined}
                >
                  <s.icon size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-5 border-t border-white/10 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <p>© {year} Patrick's Edu Care Pvt. Ltd. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" onClick={() => window.scrollTo(0, 0)} className="hover:text-[#4DD4AC] transition-colors">
              Privacy Policy
            </Link>
            <span className="text-white/20">·</span>
            <span>Made with care in Kerala 🇮🇳</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
